<script setup lang="ts">
import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';
import useLaunchGame from '~/composables/useLaunchGame';
import { useGetVertical } from '~/composables/useGaming';
import type { IGameCategoriesV4Data } from '~/interfaces/dto/games/response';
import VerticalSkeleton from '~/components/category/vertical-skeleton.vue';
const auth = useAuthStore();
const settings = useSiteStore();
const route = useRoute();
const { $t } = useNuxtApp();

const vertical = shallowRef<IGameCategoriesV4Data>({
  categories: [],
  themes: [],
  providers: [],
  isSingleCategory: false,
});
const loading = ref<boolean>(true);

const skeletonCarousels = computed(() => {
  switch (route.params.contentType) {
    case 'crashgames':
    case 'betgames':
    case 'quickgames':
      return 1;
    default:
      return 4;
  }
});

watchEffect(() => {
  const data = vertical.value;
  if (data.categories?.length > 0) {
    if (data?.isSingleCategory && data?.isLaunchGame && auth.isLoggedIn) {
      useLaunchGame({ ...data.gameToLaunch, component: 'direct' });
    }
    loading.value = false;
  }
});

onMounted(async () => {
  useGetVertical()
    .then((data) => {
      vertical.value = data;
    })
    .catch((e) => {
      settings.setLoading(false);
      loading.value = false;
      console.error(e);
    });
});
</script>

<template>
  <div>
    <LazyUserInterfaceFiltersContainer
      v-if="settings.featureDisplay({ feature: 'displayFilters' })"
    />
    <div class="container">
      <VerticalSkeleton
        v-show="loading"
        :number-of-carousels="skeletonCarousels"
      />
      <CategoryProvidersCarousel
        v-if="!!vertical?.providers && vertical?.providers?.length > 1"
        :providers="vertical?.providers"
      />
      <CategoryCarousel
        v-for="(
          { games, name, totalGames, alias }, index
        ) in vertical.categories"
        :key="index"
        :games="games"
        :alias="alias"
        :title="
          $t(
            `vertical.category.${name}-${settings.getRegionCode}`,
            `vertical.category.${name}`,
          )
        "
        :route="`/${route.params.contentType}/${alias}`"
        :display-show-all="!vertical?.isSingleCategory"
        :total-games="totalGames"
      />
      <CategoryError v-show="!vertical?.categories?.length && !loading" />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.back {
  &-arrow {
    width: 20px;
    height: 20px;
  }

  &-wrapper {
    font-size: 14px;

    &:hover {
      cursor: pointer;
    }
  }
}
</style>
